import React, { useState } from 'react';
import styled from 'styled-components';
import { Bookmark, Check, CheckCircle, ChevronDown, ChevronUp, Clock, FileText, Zap } from 'lucide-react';
import SlideLoading from './SlideLoading';
import { useDispatch, useSelector } from 'react-redux';
import { ToastProvider, useToasts } from 'common/toast';
import { useWelcomeTourData } from '../useWelcomeTourData';
import type { Recommendation } from '@/areas/culture-hub/types/api';
import {
    addRecommendationToPlan,
    getPlan,
    removeRecommendationFromPlan,
} from '@/areas/culture-hub/slices/cultureHubInterventionsSlice';
import { useCultureHubQueryParams } from '@/areas/culture-hub/hooks/useCultureHubQueryParams';
import { useEmergentThemeTeamDetailQuery } from '@/areas/culture-hub/components/interventions/action-planner/api';
import { useActionPlanOperations } from '@/areas/culture-hub/hooks/useActionPlanOperations';
import { ACTION_PLAN_TYPE_INTERVENTIONS } from '@/areas/culture-hub/constants';
import { useApiPortalV1AssessmentsPlansInterventionsDestroyMutation } from '@/store/generated/portal/assessments/plans-interventions';
import { getMemberId, getSelectedAssessmentPeriod, isCurrentMemberTeamLeader } from '@/common/selectors/auth';
import { darkenHex, lightenHex, scoreToColor } from '@/areas/dashboard/components/DashboardTeam.utils';
import { enrichRecommendationWithPillarInfo } from '@/areas/culture-hub/utils/recommendationMapper';
import { StyledAddButton } from '@/common/components/button/StyledButton';
import { SlideContainer, EmptyState, HeroBanner, BannerDecoClip, HeroLabel, HeroHeadline, HeroBody } from './slideStyles';

const ContentContainer = styled.div`
  width: min(100%, 1140px);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const HeaderPanel = styled.div`
  width: 100%;
  padding: 0.75rem 1.75rem 1.5rem;
  box-sizing: border-box;

    @media (max-width: 600px) and (orientation: portrait) {
        padding: 0.5rem 0.65rem 0.9rem;
    }
`;

const ExpandedSlideContainer = styled(SlideContainer)`
  height: 100%;
  flex: 1;
  min-height: 75vh;
`;

const LEVEL_COLORS: Record<string, { bg: string; text: string; border: string }> = {
    immediate: { bg: '#FEF2F2', text: '#B91C1C', border: '#FECACA' },
    reflection: { bg: '#EFF6FF', text: '#1D4ED8', border: '#BFDBFE' },
    team: { bg: '#F0FDF4', text: '#15803D', border: '#BBF7D0' },
    systemic: { bg: '#FDF4FF', text: '#7E22CE', border: '#E9D5FF' },
};

const levelStyle = (level: string | undefined) =>
    LEVEL_COLORS[level ?? ''] ?? { bg: '#BFDBFE', text: '#374151', border: '#E5E7EB' };

const RecContent = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  padding: 0 1.75rem 1.5rem;

    @media (max-width: 600px) and (orientation: portrait) {
        padding: 0 0.65rem 1rem;
    }
`;

const RecList = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;
  width: 100%;
  flex: 1;
  min-height: 0;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;


const RecCardWrapper = styled.div<{ $borderColor: string; $added?: boolean }>`
  position: relative;
  background: #ffffff;
  border: 1px solid transparent;
  border-radius: 0.75rem;
  padding: 1rem 1rem 1rem 1.1rem;
  display: flex;
  align-items: stretch;
  justify-content: flex-start;
  min-height: 0;
  width: 100%;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, background 0.2s ease;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
  background-image: linear-gradient(#ffffff, #ffffff), linear-gradient(135deg, rgba(59,130,246,0.16) 0%, rgba(96,165,250,0.10) 70%, rgba(148,163,184,0.08) 100%);
  background-origin: padding-box, border-box;
  background-clip: padding-box, border-box;

`;

const RecBody = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  min-height: 96px;
  gap: .75rem;
`;

const RecCardContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  overflow: hidden;
`;

const RecHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  width: 100%;
`;

const RecTitleIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #3B82F6;
  margin-right: 0.5rem;
  flex-shrink: 0;
`;

const RecTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  flex: 1 1 0;
  min-width: 0;
`;

const RecFooterRow = styled.div`
  display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
  gap: 1rem;
  align-items: flex-start;
  width: 100%;
  margin-top: 0.25rem;

    @media (max-width: 900px) {
        grid-template-columns: 1fr;
        gap: 0.5rem;
    }
`;

const RecFooterText = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-left: 1.5rem;

    @media (max-width: 900px) {
        padding-left: 0;
    }
`;

const RecDescriptionRow = styled.div`
    display: grid;
    grid-template-columns: 24px minmax(0, 1fr);
    align-items: start;
  gap: 0.5rem;
  color: #6B7280;
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  line-height: 1.5;
    width: 100%;
`;

const RecDescriptionIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 8px;
  background: #DBEAFE;
  color: #1D4ED8;
  flex-shrink: 0;
`;

const RecDetailRow = styled.div`
    display: grid;
    grid-template-columns: 24px minmax(0, 1fr);
    align-items: start;
  gap: 0.75rem;
  color: #4B5563;
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  line-height: 1.4;
    width: 100%;

    span {
        width: 100%;
        min-width: 0;
        overflow-wrap: break-word;
        word-break: normal;
    }
`;

const RecDetailIcon = styled.span<{ $bg: string; $color: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 8px;
  background: ${props => props.$bg};
  color: ${props => props.$color};
  flex-shrink: 0;
`;

const RecTitle = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: 17px;
  font-weight: 600;
  color: #111827;
  line-height: 1.35;
`;

const RecSubtitle = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  font-weight: 400;
  color: #6B7280;
  line-height: 1.5;
    min-width: 0;
    width: 100%;
    overflow-wrap: break-word;
    word-break: normal;
`;


const RecExpandedDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.75rem;
  min-width: 0;
  flex: 1 1 auto;
  width: 100%;
  padding-left: 1.5rem;

    @media (max-width: 900px) {
        padding-left: 0;
    }
`;

const RecMoreLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
    width: auto;
  justify-content: flex-end;
  border: none;
  background: transparent;
  color: #6B7280;
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 600;
  text-align: right;
  padding: 0;
  cursor: pointer;
    white-space: nowrap;
  align-self: flex-end;

  &:hover {
    color: #2563eb;
    text-decoration: underline;
  }

    @media (max-width: 900px) {
        width: auto;
        justify-content: flex-start;
        text-align: left;
        align-self: flex-start;
    }
`;


type PillarTagColors = {
    $bg: string;
    $color: string;
    $border: string;
};

const RecommendationReason = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;
`;

const RecommendationReasonLabel = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
`;

const RecommendationReasonValue = styled.div`
  width: 100%;
  min-width: 0;
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  line-height: 1.45;
  font-weight: 400;
  color: #6B7280;
    overflow-wrap: break-word;
    word-break: normal;
`;

const RecActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-width: 110px;
  height: 24px;
  border: none;
  border-radius: 8px;
  padding: 0 12px 0px 0px;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  color: #166534;
  background: #ECFDF5;
  transition: background 0.2s ease, transform 0.2s ease;

  &:hover {
    background: #DCFCE7;
    transform: translateY(-1px);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.65;
    transform: none;
  }
`;

const RecActionIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #DCFCE7;
  color: #16A34A;
  flex-shrink: 0;
`;

const EmptyCard = styled(EmptyState)`
  background: #F9FAFB;
  border: 1px solid #E5E7EB;
  border-radius: 0.75rem;
`;

const FooterNote = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #4B5563;
  justify-content: center;
  padding: 0rem 1.25rem;
  line-height: 1.4;
  border-radius: 0.75rem;
  margin-top: 1.5rem;
`;


const RecCard: React.FC<{
    rec: Recommendation;
    rank: number;
    isAdded: boolean;
    isSaving: boolean;
    onAdd: (rec: Recommendation) => void;
    pillarTagColors: PillarTagColors;
    themeLabel?: string;
    disableAdd?: boolean;
    addDisabledReason?: string;
}> = ({ rec, rank, isAdded, isSaving, onAdd, pillarTagColors, themeLabel, disableAdd = false, addDisabledReason }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const title = rec.task || rec.task_title || rec.title || rec.recommendation_name || 'Recommended Action';
    const subtitle = rec.why || rec.description || '';
    const recommendationTheme = themeLabel;
    const time = rec.time || rec.time_estimate || rec.duration || '';
    const softSkillsValue = rec.softSkills || '';
    const hasDetails = Boolean(subtitle || time || softSkillsValue);
    const colors = levelStyle(rec.level);

    return (
        <RecCardWrapper $borderColor={colors.border} $added={isAdded}>
            <div style={{ display: 'flex', gap: '.75rem', flex: 1, alignItems: 'stretch' }}>
                <RecBody>
                    <RecCardContent>
                        <RecHeaderRow>
                            <RecTitleWrapper>
                                <RecTitleIcon>
                                    <Bookmark
                                        size={18}
                                        color={'#3B82F6'}
                                        fill={isAdded ? 'currentColor' : 'none'}
                                    />
                                </RecTitleIcon>
                                <RecTitle>{title}</RecTitle>
                            </RecTitleWrapper>
                            {isAdded ? (
                                <RecActionButton
                                    disabled={isSaving}
                                    onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                                        event.stopPropagation();
                                        onAdd(rec);
                                    }}
                                >
                                    <RecActionIcon>
                                        <CheckCircle size={14} />
                                    </RecActionIcon>
                                    Added to Plan
                                </RecActionButton>
                            ) : (
                                <span title={disableAdd ? addDisabledReason : undefined} style={{ display: 'inline-block' }}>
                                    <StyledAddButton
                                        label="Add to Plan"
                                        color="green"
                                        width="110px"
                                        height="24px"
                                        disabled={isSaving || disableAdd}
                                        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                                            event.stopPropagation();
                                            onAdd(rec);
                                        }}
                                    />
                                </span>
                            )}
                        </RecHeaderRow>
                        <RecFooterText>
                            <RecommendationReason>
                                <RecommendationReasonLabel>Based on your team's feedback...</RecommendationReasonLabel>
                                <RecommendationReasonValue>{recommendationTheme || 'Recommended for your team'}</RecommendationReasonValue>
                            </RecommendationReason>
                        </RecFooterText>
                        <RecFooterRow>
                            <RecExpandedDetails>
                                {isExpanded && (
                                    <>
                                        {subtitle ? (
                                            <RecDescriptionRow>
                                                <RecDescriptionIcon>
                                                    <FileText size={14} />
                                                </RecDescriptionIcon>
                                                <RecSubtitle>{subtitle}</RecSubtitle>
                                            </RecDescriptionRow>
                                        ) : null}
                                        {(time || softSkillsValue) && (
                                            <RecDetailRow>
                                                <RecDetailIcon $bg="#DCFCE7" $color="#15803D">
                                                    <Clock size={14} />
                                                </RecDetailIcon>
                                                <span>{time || 'Not specified'}</span>
                                            </RecDetailRow>
                                        )}
                                        {softSkillsValue && (
                                            <RecDetailRow>
                                                <RecDetailIcon $bg="#FEE2B8" $color="#C2410C">
                                                    <Check size={14} />
                                                </RecDetailIcon>
                                                <span>{softSkillsValue}</span>
                                            </RecDetailRow>
                                        )}
                                    </>
                                )}
                            </RecExpandedDetails>
                            {hasDetails && (
                                <RecMoreLink
                                    type="button"
                                    onClick={() => setIsExpanded((prev) => !prev)}
                                >
                                    {isExpanded ? (
                                        <>
                                            <ChevronUp size={14} />
                                            Show less
                                        </>
                                    ) : (
                                        <>
                                            <ChevronDown size={14} />
                                            Learn more
                                        </>
                                    )}
                                </RecMoreLink>
                            )}
                        </RecFooterRow>
                    </RecCardContent>
                </RecBody>
            </div>
        </RecCardWrapper>
    );
};

const Slide7NextStepsContent: React.FC = () => {
    const dispatch = useDispatch();
    const selectedAssessmentPeriod = useSelector(getSelectedAssessmentPeriod);
    const plan = useSelector(getPlan);
    const currentUserId = useSelector(getMemberId) as number | null;
    const isTeamLeader = useSelector(isCurrentMemberTeamLeader);
    const { focusRecommendations, pillars, isLoading } = useWelcomeTourData() as {
        focusRecommendations: Recommendation[];
        pillars: any[];
        isLoading: boolean;
    };
    const { assessmentPeriodId, organizationId, teamId, isMostRecentClosedAssessmentPeriod } = useCultureHubQueryParams();
    const [savingTaskKey, setSavingTaskKey] = useState<string | null>(null);
    const addToPlanDisabledReason = !isMostRecentClosedAssessmentPeriod
        ? 'You can only add focus plan items to the current assessment period.'
        : undefined;

    const { data: themeTeamData, isLoading: themeLoading, isFetching: themeFetching } = useEmergentThemeTeamDetailQuery(
        { assessmentPeriodId: assessmentPeriodId as number, teamId },
        { skip: !assessmentPeriodId || !teamId }
    );

    const isRecommendationThemesLoading = themeLoading || themeFetching;
    const recommendationThemeMap = React.useMemo(() => {
        const map = new Map<string, string>();
        const themes = themeTeamData?.themes ?? [];
        themes.forEach((theme) => {
            const title = theme.theme_description?.trim() || '';
            theme.suggested_interventions?.forEach((suggestion) => {
                if (suggestion.recommendation_template_id != null && suggestion.task_index != null && title) {
                    map.set(`${suggestion.recommendation_template_id}_${suggestion.task_index}`, title);
                }
            });
        });
        return map;
    }, [themeTeamData]);

    const [deletePlan] = useApiPortalV1AssessmentsPlansInterventionsDestroyMutation();

    const { saveAllTasks, determineLeaderAndCategory } = useActionPlanOperations({
        selectedAssessmentPeriodId: assessmentPeriodId,
        organizationId,
        currentUserId: currentUserId ?? null,
        currentUserDebriefSchedule: null,
        categoryDelegates: [],
        existingActionPlans: undefined,
        actionPlanType: ACTION_PLAN_TYPE_INTERVENTIONS,
        teamId,
    });

    const { addToast } = useToasts();

    const getTaskKey = (rec: Recommendation) => `${rec.recommendation_template_id ?? 'unknown'}_${rec.task_index ?? 0}`;

    const getPillarScore = (rec: Recommendation): number | null => {
        const lookup = (rec.pillar_key || (rec.pillarKey as string) || rec.pillarName || rec.pillar_name || '').toString().trim().toLowerCase();
        const pillar = pillars.find((p) => p.key?.toString().toLowerCase() === lookup || p.name?.toString().toLowerCase() === lookup);
        return typeof pillar?.score === 'number' ? pillar.score : null;
    };

    const getPillarTagColors = (rec: Recommendation): PillarTagColors => {
        const score = getPillarScore(rec);
        if (score === null) {
            return { $bg: '#ede9fe', $color: '#6d28d9', $border: '#ddd6fe' };
        }
        const baseColor = scoreToColor(score);
        const lightBg = lightenHex(baseColor, 0.15);
        const lightBorder = lightenHex(baseColor, 0.9);
        const textColor = darkenHex(baseColor, 0.75) || baseColor;
        return {
            $bg: lightBg || baseColor,
            $color: textColor,
            $border: lightBorder || baseColor,
        };
    };

    const getPlanItem = (rec: Recommendation) =>
        plan.find(
            (item) =>
                item.recommendation_template_id === rec.recommendation_template_id &&
                (item.task_index ?? 0) === (rec.task_index ?? 0)
        );

    const onAddRecommendation = async (rec: Recommendation) => {
        if (!assessmentPeriodId || !organizationId || !teamId) {
            addToast('Unable to save focus area: missing assessment or organization context.', { appearance: 'error', autoDismiss: true });
            return;
        }

        if (!isMostRecentClosedAssessmentPeriod) {
            addToast('You can only add focus plan items to the current assessment period.', { appearance: 'error', autoDismiss: true });
            return;
        }

        const taskKey = getTaskKey(rec);
        const existingPlanItem = getPlanItem(rec);
        const isAlreadyAdded = Boolean(existingPlanItem);

        if (isAlreadyAdded) {
            if (!existingPlanItem) {
                addToast('Unable to remove focus area from your plan.', { appearance: 'error', autoDismiss: true });
                return;
            }

            setSavingTaskKey(taskKey);
            try {
                if ((existingPlanItem as any).action_plan_id) {
                    await deletePlan({ id: (existingPlanItem as any).action_plan_id }).unwrap();
                }
                dispatch(removeRecommendationFromPlan({
                    recommendation_template_id: rec.recommendation_template_id,
                    task_index: rec.task_index ?? 0,
                }));
                addToast('Focus area removed from your plan.', { appearance: 'success', autoDismiss: true });
            } catch (error) {
                console.error('Failed to remove focus area from guided experience:', error);
                addToast('Failed to remove focus area. Please try again.', { appearance: 'error', autoDismiss: true });
            } finally {
                setSavingTaskKey(null);
            }
            return;
        }

        setSavingTaskKey(taskKey);

        const enriched = enrichRecommendationWithPillarInfo(rec, {
            key: rec.pillar_key || (rec.pillarKey as string) || '',
            name: rec.pillarName || '',
            score: 0,
            org_avg: 0,
            trend: 0,
            qual_priority: 0,
            themes: [],
            recommendations: [],
        });

        try {
            const { leaderId, categoryUid } = determineLeaderAndCategory();
            const result = await saveAllTasks([enriched], leaderId, categoryUid);
            if (!result.success) {
                addToast(result.error || 'Failed to save focus area.', { appearance: 'error', autoDismiss: true });
                return;
            }

            dispatch(addRecommendationToPlan(enriched));
            addToast('Focus area saved to your plan.', { appearance: 'success', autoDismiss: true });
        } catch (error) {
            console.error('Failed to save focus area from guided experience:', error);
            addToast('Failed to save focus area. Please try again.', { appearance: 'error', autoDismiss: true });
        } finally {
            setSavingTaskKey(null);
        }
    };

    const showLoadingOverlay = isLoading || isRecommendationThemesLoading;
    const loadingMessage = isLoading ? 'Loading recommendations…' : 'Loading recommendation themes…';

    return (
        <ExpandedSlideContainer>
            <HeroBanner>
                <BannerDecoClip />
                {showLoadingOverlay && <SlideLoading message={loadingMessage} />}
                <ContentContainer>
                    <HeaderPanel>
                        <HeroLabel>Next steps - { selectedAssessmentPeriod?.name || 'Current Assessment Period' }</HeroLabel>
                        <HeroHeadline>Here's where to start</HeroHeadline>
                        <HeroBody>Based on your results, these focus area projects will have the biggest impact on your team culture.</HeroBody>
                    </HeaderPanel>

                    <RecContent>
                    <RecList>
                        {focusRecommendations.length === 0 ? (
                            <EmptyCard>Recommendations will appear once your assessment is processed.</EmptyCard>
                        ) : (
                            focusRecommendations.map((rec: Recommendation, i: number) => {
                                const isAdded = plan.some(
                                    (actionPlan) =>
                                        actionPlan.recommendation_template_id === rec.recommendation_template_id &&
                                        (actionPlan.task_index ?? 0) === (rec.task_index ?? 0)
                                );
                                return (
                                    <RecCard
                                        key={i}
                                        rec={rec}
                                        rank={i + 1}
                                        isAdded={isAdded}
                                        isSaving={savingTaskKey === getTaskKey(rec)}
                                        onAdd={onAddRecommendation}
                                        pillarTagColors={getPillarTagColors(rec)}
                                        themeLabel={recommendationThemeMap.get(`${rec.recommendation_template_id ?? 'unknown'}_${rec.task_index ?? 0}`)}
                                        disableAdd={!isMostRecentClosedAssessmentPeriod}
                                        addDisabledReason={addToPlanDisabledReason}
                                    />
                                );
                            })
                        )}
                    </RecList>

                    {!isLoading && focusRecommendations.length > 0 && !isMostRecentClosedAssessmentPeriod && (
                        <FooterNote>
                            <Clock size={13} color='#414141' />
                            You can only add focus plan items to the current assessment period.
                        </FooterNote>
                    )}

                    {!isLoading && focusRecommendations.length > 0 && (
                        <FooterNote>
                            <Zap size={13} color='#414141' />
                            You can make changes to your plan or explore additional recommendations in the Focus Plan area at any time.
                        </FooterNote>
                    )}
                </RecContent>
                </ContentContainer>
            </HeroBanner>

        </ExpandedSlideContainer>
    );
};

const Slide5NextStepsWrapper: React.FC = () => (
    <ToastProvider autoDismiss autoDismissTimeout={1500} transitionDuration={300}>
        <Slide7NextStepsContent />
    </ToastProvider>
);

export default Slide5NextStepsWrapper;
